import classNames from 'classnames';
import {
  AddressBlock,
  Button,
  ContentBlock,
  FlexContainer,
  MapRender,
  TimeLine,
  TitledBlock,
  Txt,
  Modal,
  ToDoHeading,
  Icon,
  PropertyBaseDetails,
  LoadingPlaceholder,
  VerifyPassword,
  TenantAddEdit,
  UserInfoBlock,
} from 'components';
import { depositStatusMap } from 'config/types';
import { fromSwiftDate } from 'helpers/swiftDateFormatter';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Marker } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { ModalGlobal } from 'lib/ModalContext';
import { useProperty, useDataActions, useTenancy, useProfile } from 'state';
import styles from './TenancyViewDetails.module.scss';

/**
 * TenancyViewDetails
 *
 * @return {JSX.Element}
 */
const TenancyViewDetails = ({ className }) => {
  const profileState = useProfile();
  const propertyDoc = useProperty();
  const tenancyDoc = useTenancy();
  const { tenants: tenantsDocsArray } = tenancyDoc;
  const storageActions = useDataActions();

  const [editTenant, setEditTenant] = useState(null);
  const [terminateVerification, setTerminateVerification] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const { data: tenancy } = tenancyDoc;
  const { data: property } = propertyDoc;

  const latLng = {
    lat: property.address?.lat,
    lng: property.address?.long,
  };

  // const handleGoToLinkBankAccount = () => {
  //   console.log('Go to bank');
  //   navigateToAndBackHere('/payments/addBankAccount');
  // };

  const handleGoToTodo = () => {
    navigate('../../todo');
  };

  const handleTerminateVerification = () => setTerminateVerification(true);
  const handleCloseTerminateVerification = () =>
    setTerminateVerification(false);

  const handleTerminate = async () => {
    handleCloseTerminateVerification();
    setIsLoading(true);
    await storageActions.tenancy.terminateTenancy(tenancyDoc);
    navigate('../../..', { replace: true });
  };

  const tenancyStartDate = fromSwiftDate(tenancy.dates?.startDate);
  const tenancyEndDate = fromSwiftDate(tenancy.dates?.endDate);

  const handleEditTenant = (t) => () => setEditTenant(t);
  const closeEditTenant = () => setEditTenant(null);

  const handleSaveTenant = (updatedTenant) => {
    console.log('Save tenant', updatedTenant);
    storageActions.tenancy.editTenant(editTenant, updatedTenant);
    setEditTenant(null);
  };

  const todoItems = [
    tenancyDoc.agreementSigned,
    tenancyDoc.tenantsLinked,
    tenancyDoc.tenantsDepositPaid,
    tenancyDoc.tenantsStandingOrdersSetup,
    tenancyDoc.depositRegistered,
    tenancyDoc.depositProtected,
    tenancyDoc.propertyIsOccupied,
  ];

  const toItemsCompleted = todoItems.filter(Boolean);

  return (
    <FlexContainer
      className={classNames(
        styles.TenancyViewDetails,
        className,
        TenancyViewDetails.displayName
      )}
      column
    >
      <TitledBlock>
        <ToDoHeading
          title2="Tasks"
          description2="Here you can manage all of your tenancy tasks in one place."
          completed={toItemsCompleted.length}
          completedOf={todoItems.length}
          onClick={handleGoToTodo}
          black
          noDivider
        />
      </TitledBlock>
      <TitledBlock title="Landlord">
        <FlexContainer gap wrap>
          <UserInfoBlock dataState={profileState} />
        </FlexContainer>
      </TitledBlock>
      <TitledBlock title="Tenants">
        <FlexContainer gap wrap>
          {tenantsDocsArray.isInitialized ? (
            tenantsDocsArray.data?.length ? (
              tenantsDocsArray.data.map((t) => (
                <UserInfoBlock
                  key={t.id}
                  onClick={handleEditTenant(t)}
                  dataState={t}
                />
              ))
            ) : (
              <Txt regular>No tenants</Txt>
            )
          ) : (
            <LoadingPlaceholder spinner />
          )}
        </FlexContainer>
      </TitledBlock>
      <TitledBlock title="Timeline">
        <ContentBlock column>
          <TimeLine dateStart={tenancyStartDate} dateEnd={tenancyEndDate} />
        </ContentBlock>
      </TitledBlock>
      <TitledBlock title="Deposit account number">
        <ContentBlock alignCenter>
          <FlexContainer column gapHalf flex>
            <Txt m caption>
              STATUS - {depositStatusMap[tenancy.deposit?.status] || 'Unknown'}
            </Txt>
            <Txt xx>{tenancy.payments.depositReference}</Txt>
          </FlexContainer>
          <FlexContainer>
            <Txt caption>
              <Icon name="secure" />
            </Txt>
          </FlexContainer>
        </ContentBlock>
      </TitledBlock>
      <TitledBlock title="Address">
        <FlexContainer gap column>
          <AddressBlock address={property.address} />
          <MapRender center={latLng} zoom={14}>
            <Marker position={latLng} />
          </MapRender>
        </FlexContainer>
      </TitledBlock>
      <PropertyBaseDetails property={property} />
      {tenancyDoc.isActive ? (
        <>
          <Button
            disabled={isLoading}
            warn
            onClick={handleTerminateVerification}
          >
            Terminate Tenancy
          </Button>
          {terminateVerification ? (
            <Modal
              onClose={handleCloseTerminateVerification}
              title="Please confirm tenancy termination"
              showCloseButton
            >
              <VerifyPassword onSuccess={handleTerminate} />
            </Modal>
          ) : null}
        </>
      ) : null}
      {editTenant ? (
        <ModalGlobal>
          <Modal onClose={closeEditTenant} noPadding>
            <TenantAddEdit tenant={editTenant.data} onSave={handleSaveTenant} />
          </Modal>
        </ModalGlobal>
      ) : null}
    </FlexContainer>
  );
};

TenancyViewDetails.displayName = 'TenancyViewDetails';

export default observer(TenancyViewDetails);
